import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ApiService } from '../../shared/services/core/api.service';
import { DatePipe } from '@angular/common';
import { LoggingService } from '../../shared/services/core/logging.service';
import { MemberService } from '../../account/member/member.service';
import { MemberStore } from '../../account/member/member.store';
import { UserContext } from '../../account/member/models/user-context.model';
import { LoginResponse } from '../models/login-response.model';
import { MlbamLoginResponse } from '../models/mlbam-login-response.model';
import { MultipleAccountDetailModel } from '../models/multiple-account-detail.model';
import { Router } from '@angular/router';
import { ApiLoginModel } from '../models/api-login.model';
import { config } from '../../shared/config';
import { MlbamLoginResponseStatus } from '../models/mlbam-login-response-status.model';
import { InputType, ProviderName } from '../../account/models/login-response.model';
import { OutputField } from '../models/outputfield.model';
import { KeyValuePair } from '../../shared/models/key-value-pair.model';
import { MemberCardService } from '../../account/member-card/member-card.service';
import { ChildService } from '../../guardian/child.service';
import { ChildStore } from '../../guardian/child.store';
import { CardService } from '../../cards/card.service';
import { CardStore } from '../../cards/card.store';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { MemberQuery } from '../../account/member/member.query';
import { PopulationService } from '../../club/population/population.service';
import { PopulationQuery } from '../../club/population/population.query';
import { HttpParams } from '@angular/common/http';
@Injectable()
export class AuthService {
  constructor(
    public jwtHelper: JwtHelperService,
    private api: ApiService,
    private logService: LoggingService,
    private cookieService: CookieService,
    private datepipe: DatePipe,
    private memberService: MemberService,
    private router: Router,
    private memberCardService: MemberCardService,
    private memberStore: MemberStore,
    private childService: ChildService,
    private childStore: ChildStore,
    private cardService: CardService,
    private cardStore: CardStore,
    private memberQuery: MemberQuery,
    private populationService: PopulationService,
    private populationQuery: PopulationQuery
  ) {}

  /** Check if a user is authenticated. */
  isAuthenticated(): boolean {
    if (this.hasAccessToken() && this.hasCurrentRefreshToken()) {
      return true;
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
      return false;
    }
  }

  /** Check if api is in maintenance mode or having other issues. */
  checkApiStatus(clubId: number = 1) {
    this.checkMaintenceMode().subscribe(
      (result) => {
        this.logService.info('API Available');
      },
      (error) => {
        if (error.status === 503) {
          this.logService.warning('503 [Service Unavailable] received; Maintenance mode is probably on', error);
          this.router.navigate(['maintenance']);
        } else {
          this.logService.error('500 [Internal API Error] received', error);
          this.router.navigate(['maintenance']);
        }
      }
    );
  }

  checkMaintenceMode(clubId: number = 1): Observable<any> {
    return this.api.get<any>(`service/status/${clubId}`);
  }

  /** Check if the user has an access token. */
  hasAccessToken(): boolean {
    const token = this.getAccessToken();
    if (token) {
      if (!this.memberQuery.getValue().user) {
        let user = this._makeUserContext(token);

        /**
         * If the user has picked a locale already we should use that
         */
        const lcid = localStorage.getItem('lcid');
        if (lcid) {
          user.lcid = lcid;
        }

        this.memberService.setUserContext(user);
      }

      return true;
    } else {
      return false;
    }
  }

  /** Check if the user has a valid refresh token. */
  hasCurrentRefreshToken(): boolean {
    const token = localStorage.getItem('refresh');
    if (token) {
      return !this.jwtHelper.isTokenExpired(token);
    } else {
      return false;
    }
  }

  /** Make a request to refresh the curent user's access token using the refresh token. */
  refreshAccessToken(): Observable<LoginResponse> {
    const token = localStorage.getItem('refresh');
    return this.api.post<any>('token/refresh', `"${token}"`).pipe(
      tap((response) => {
        let token = this.jwtHelper.decodeToken(response.AccessToken);

        localStorage.setItem('token', response.AccessToken);
        localStorage.setItem('refresh', response.RefreshToken);
        localStorage.setItem('lcid', token.lcid);
      })
    );
  }

  /** Refresh the access token AND rebuild the stored user context from it. */
  refreshAccessTokenAndContext(): Observable<LoginResponse> {
    return this.refreshAccessToken().pipe(
      tap((response) => this.memberService.setUserContext(this._makeUserContext(response.AccessToken)))
    );
  }

  /** Login as a user without a username and password. */
  ssoLogin(
    entityType: number,
    entity: string,
    loginDate: Date,
    hashedValue: string,
    clubId: number,
    loginType?: number,
    returnUrl?: string,
    providerId?: number,
    clientTypeId?: number,
    locale?: string,
    clientAttribute?: string
  ) {
    if (loginType !== undefined && isNaN(loginType)) {
      loginType = 1;
    }

    if (providerId !== undefined && isNaN(providerId)) {
      providerId = -1;
    }

    if (clientTypeId !== undefined && isNaN(clientTypeId)) {
      clientTypeId = 0;
    }

    let params = new HttpParams();

    if (locale) {
      params = params.append('locale', locale);
    }

    if (clientAttribute) {
      params = params.append('ClientAttribute', clientAttribute);
    }

    this.logService.debug('Logging on with SSO', {
      entityType,
      entity,
      loginDate,
      hashedValue,
      clubId,
      loginType,
      returnUrl,
      providerId,
      clientTypeId,
      locale,
      clientAttribute,
    });

    return this.api
      .post<any>(
        'sso/validatesso?entityType=' +
          entityType +
          '&entity=' +
          entity +
          '&loginDate=' +
          this.datepipe.transform(loginDate, 'yyyy-MM-dd HH:mm:ss') +
          '&hashedValue=' +
          hashedValue +
          '&clubId=' +
          clubId +
          '&loginType=' +
          loginType +
          '&returnUrl=' +
          returnUrl +
          '&providerId=' +
          providerId +
          '&clientTypeId=' +
          clientTypeId,
        {},
        params
      )
      .pipe(
        tap((authResponse) => {
          if (authResponse) {
            const accessToken = authResponse.AccessToken;
            this.loginUsingToken(accessToken, authResponse.RefreshToken);
          }
        })
      );
  }

  /** Returns a observable of Logging into third party */
  loginThirdPartyObservable(providerId: ProviderName, clubId: number, options: KeyValuePair[]): Observable<OutputField[]> {
    this.logService.info('Attempting to log off to ', providerId);
    return this.api.post(`provider/thirdpartyoff/${clubId}/${providerId}`, options);
  }

  /** Logs into the third party */
  loginThirdParty(
    providerId: ProviderName,
    clubId: number,
    elementName: string = '',
    openInNewWindow: boolean = false,
    options: KeyValuePair[] = []
  ) {
    this.loginThirdPartyObservable(providerId, clubId, options)
      .pipe(
        tap((data) => {
          let outputs = data as OutputField[];

          const hasHiddens = outputs.find((x) => x.InputType == InputType.Hidden);
          const hasUrl = outputs.find((x) => x.InputType == InputType.Url);
          const hasQuery = outputs.find((x) => x.InputType == InputType.QueryString);
          const hasJavascript = outputs.find((x) => x.InputType == InputType.Javascript);
          let target = '_self';
          if (openInNewWindow) {
            target = '_blank';
          }

          let pageForm = document.getElementById(elementName) as HTMLElement;
          if (!pageForm) {
            pageForm = document.body;
          }

          if ((hasHiddens != null || hasQuery != null) && hasUrl != null) {
            if (pageForm) {
              let sendForm =
                "<form action='" +
                hasUrl.Value +
                "' method='post' name='form' Id=\"SingleSignOnRedirect\" target='" +
                target +
                "' >";
              outputs.forEach((item) => {
                if (item.InputType == InputType.Hidden) {
                  sendForm += "<input type='hidden' name='" + item.FieldName + "' value='" + item.Value + "' />";
                }
              });
              sendForm += "<script>document.getElementById('SingleSignOnRedirect').submit();</script>";
              pageForm.innerHTML = sendForm;
            }
            let renderForm = document.getElementById('SingleSignOnRedirect') as HTMLFormElement;
            renderForm.submit();
          } else if (hasJavascript != null) {
            let sendForm = '';
            outputs.forEach((item) => {
              if (item.InputType == InputType.Javascript) {
                sendForm += "<input type='hidden' name='" + item.FieldName + "' value='" + item.Value + "' />";
              }
            });
            pageForm.innerHTML = sendForm;
          } else if (hasQuery != null) {
            window.open(hasQuery.Value, target);
          } else if (hasUrl != null) {
            window.open(hasUrl.Value, target);
          }
        })
      )
      .toPromise();
  }

  /** Set the auth token to the token provided in the 'portal-token' cookie. */
  sso() {
    let token = this.cookieService.get('portal-token');
    let user = this._makeUserContext(token);
    this.memberService.setUserContext(user);
    localStorage.setItem('token', token);
  }

  /**
   * Will log in the customer using the access token that has already been give to the customer
   *
   * @param accessToken Access Token from the login call
   * @param refreshToken Refresh token from the login call
   * @param redirectUrl if passed in and the redirectUrl is not undefined will navigate to that url
   */
  loginUsingToken(accessToken: string, refreshToken: string, redirectUrl: string | undefined = undefined) {
    let user = this._makeUserContext(accessToken);
    this.memberService.setUserContext(user);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh', refreshToken);
    localStorage.setItem('lcid', user.lcid);
    sessionStorage.removeItem('UnauthorizedEntityGuid');

    if (redirectUrl !== undefined) {
      this.router.navigate([redirectUrl]);
    }
  }

  /**
   * Will log in the customer using the access token that has already been give to the customer, while also refreshing store values
   *
   * @param accessToken Access Token from the login call
   * @param refreshToken Refresh token from the login call
   * @param redirectUrl if passed in and the redirectUrl is not undefined will navigate to that url
   */
  switchUserLoginUsingToken(accessToken: string, refreshToken: string, redirectUrl: string | undefined = undefined) {
    let user = this._makeUserContext(accessToken);
    this.memberService.setUserContext(user);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh', refreshToken);
    localStorage.setItem('lcid', user.lcid);

    //clear stores and refetch data to ensure data integrity when swapping accounts
    this.memberStore.setHasCache(false);
    this.memberService.fetchMemberDetails().toPromise();
    this.memberCardService.fetchCard().toPromise();

    this.childStore.setHasCache(false);
    this.childService.fetchChildren().toPromise();
    this.childService.fetchGuardian().toPromise();

    this.cardStore.setHasCache(false);
    this.cardService.fetchCards();

    if (redirectUrl !== undefined) {
      this.router.navigate([redirectUrl]);
    }
  }

  /** Login using the standard login flow. */
  login(username: string, password: string): Observable<LoginResponse> {
    this.logService.info('Attempting to login as user', username);
    let loginRequest = this.api.post<any>('token', { username, password });
    let unauthorizedEntityHeader = this.getUnauthorizedEntityHeader();

    if (unauthorizedEntityHeader) {
      loginRequest = this.api.postWithCustomHeaders<any>('token', { username, password }, unauthorizedEntityHeader);
    }

    return loginRequest.pipe(
      tap((authResponse) => {
        if (authResponse) {
          this.loginUsingToken(authResponse.AccessToken, authResponse.RefreshToken);
        }
      })
    );
  }

  /**
   * Login using the MLBAM login flow.
   * @param username The username of the account to login with.
   * @param password The password for the account.
   * @param deviceGUID A GUID that represents the current device (without any hyphens).
   * @param successRedirectUrl The relative path the user should be redirected to on successful login.
   * @param chooseAccountRedirectUrl The relative path the user should be redirected to if there are multiple accounts for them to choose between.
   * @param createAccountRedirectUrl The relative path the user should be redirected to if they are required to create an account.
   */
  mlbamLogin(
    username: string,
    password: string,
    deviceGUID: string,
    successRedirectUrl: string = '/',
    chooseAccountRedirectUrl: string = '/login/accounts',
    createAccountRedirectUrl: string = '/register/new-member'
  ): Observable<MlbamLoginResponse> {
    this.logService.info('Attempting to login as MLBAM user', username);

    const apiLoginModel: ApiLoginModel = {
      LoginFields: { username, password, deviceGUID },
      PortalUrl: config.portalUrl,
    };

    return this.api.post<any>('token/mlbam', apiLoginModel).pipe(
      tap((authResponse: MlbamLoginResponse) => {
        if (authResponse) {
          if (authResponse.Tokens) {
            // Tokens returned so we can log straight in as a user
            this.logService.info('Tokens returned - logging in...');
            this.loginUsingToken(authResponse.Tokens.AccessToken, authResponse.Tokens.RefreshToken, successRedirectUrl);
          } else if (authResponse.AvailableAccounts && authResponse.AvailableAccounts.length > 0) {
            // Multiple accounts are linked to this MLBAM account so the user must choose one to continue
            this.logService.info('Multiple accounts returned, selection is required to continue...');
            this.memberService.setAvailableAccounts(authResponse.AvailableAccounts);
            this.router.navigate([chooseAccountRedirectUrl]);
          } else if (authResponse.Status === MlbamLoginResponseStatus.CreateAccount) {
            // username and password is successful but no translation exists so create it
            this.logService.info('Account needs to be created');
            this.router.navigate([createAccountRedirectUrl]);
          } else {
            this.logService.error('Login response contains no tokens and no available accounts to choose from.', authResponse);
          }
        } else {
          this.logService.error('Login response is not type MlbamLoginResponse.', authResponse);
        }
      })
    );
  }

  /**
   * Exchange a one-time auth code received from the SSO redirect URL for a JWT access token,
   * then log the user in. Used for the standard (non multi-account) code exchange flow.
   * The code is single-use and is consumed by this call.
   * @param code The auth code received as the `token` query parameter in the SSO redirect URL.
   * @param successRedirectUrl The relative path the user should be redirected to on successful login.
   */
  exchangeCodeForToken(code: string, successRedirectUrl: string = '/'): Observable<LoginResponse> {
    this.logService.info('Exchanging auth code for token...');

    let params = new HttpParams().set('code', code);

    return this.api.post<any>('token/code', {}, params).pipe(
      tap((authResponse: LoginResponse) => {
        if (authResponse && authResponse.AccessToken) {
          this.logService.info('Auth code exchanged - logging in...');
          this.loginUsingToken(authResponse.AccessToken, authResponse.RefreshToken, successRedirectUrl);
        } else {
          this.logService.error('Auth code exchange returned no access token.', authResponse);
        }
      })
    );
  }

  /**
   * Exchange an MLBAM auth code for the list of linked member accounts, without consuming the code.
   * The returned code remains valid so the user's selected account can be submitted afterwards via
   * {@link mlbamLoginWithAccount}.
   * @param code The MLBAM auth code received from the SSO redirect URL `token` parameter.
   */
  getMlbamLinkedAccounts(code: string): Observable<MultipleAccountDetailModel> {
    this.logService.info('Fetching linked MLBAM accounts for auth code...');
    return this.api.post<MultipleAccountDetailModel>(`token/mlbam/${code}`, {} as any);
  }

  /**
   * Complete the MLBAM login for the account the user selected. Consumes the auth code and, on success,
   * logs the user in using the returned tokens.
   * @param code The same MLBAM auth code used to list the linked accounts.
   * @param externalRefNumber The ExternalRefNumber of the account the user selected.
   * @param successRedirectUrl The relative path the user should be redirected to on successful login.
   */
  mlbamLoginWithAccount(code: string, externalRefNumber: string, successRedirectUrl: string = '/'): Observable<LoginResponse> {
    this.logService.info('Completing MLBAM login for selected account', externalRefNumber);

    return this.api.post<any>(`token/mlbam/${code}/${externalRefNumber}`, {}).pipe(
      tap((authResponse: LoginResponse) => {
        if (authResponse && authResponse.AccessToken) {
          this.logService.info('MLBAM account login successful - logging in...');
          this.loginUsingToken(authResponse.AccessToken, authResponse.RefreshToken, successRedirectUrl);
        } else {
          this.logService.error('MLBAM account login returned no access token.', authResponse);
        }
      })
    );
  }

  /** Log the current user out. */
  logout(providerId: ProviderName = ProviderName.Fortress, providerTokens: { [key: string]: string | null } = {}) {
    this.logService.info('Logging out...');
    const token = localStorage.getItem('refresh');
    if (providerId !== ProviderName.Fortress && token) {
      let combinedTokens = { refreshToken: token, ...providerTokens };
      return this.api.post<any>(`token/logout/${providerId}`, JSON.stringify(combinedTokens)).pipe(
        tap(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh');
        })
      );
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
      return this.api.post<any>('token/logout', `"${token}"`);
    }
  }

  /** Switch to one of the user's other members by member ID. */
  switchUser(memberId: string, reload: boolean = true): Observable<any> {
    this.logService.info('Switching to user with member ID', memberId);
    const refreshToken = localStorage.getItem('refresh');

    return this.api.post<any>(`sso/switchUser?entity=${memberId}&entityType=3`, `"${refreshToken}"`).pipe(
      tap((token) => {
        this.switchUserLoginUsingToken(token.AccessToken, token.RefreshToken);
        if (reload) {
          location.reload();
        }
      })
    );
  }

  /** Switch to one of the user's other members by member ID. */
  switchToGuardian(redirectUrl: string = '/'): Observable<any> {
    this.logService.info('Switching to daddy');
    const refreshToken = localStorage.getItem('refresh');

    return this.api.post<any>(`sso/switchtoguardian`, `"${refreshToken}"`).pipe(
      tap((token) => {
        this.switchUserLoginUsingToken(token.AccessToken, token.RefreshToken, redirectUrl);
      })
    );
  }

  /** Decode the auth token and build a user context model. */
  private _makeUserContext(token: any): UserContext {
    let decodedToken = this.jwtHelper.decodeToken(token);

    let user = new UserContext();
    user.externalRef = decodedToken.external_ref;
    user.lcid = decodedToken.lcid;
    user.memberId = decodedToken.member_id;
    user.populationId = decodedToken.population_id;
    if (!decodedToken.population_name) {
      this.populationService
        .fetchPopulations()
        .toPromise()
        .then(() => {
          user.populationName = this.populationQuery.getEntity(user.populationId)?.DisplayName;
        });
    } else {
      user.populationName = decodedToken.population_name;
    }

    user.portalId = decodedToken.portal_id;
    user.clubId = +decodedToken.clubId;
    user.sessionId = decodedToken.session_id;
    user.status = +decodedToken.account_status;
    user.clientType = +decodedToken.client_type;
    user.loginType = +decodedToken.login_type;
    user.clubs = decodedToken?.clubs;
    user.clientAttribute = decodedToken.client_attribute;
    user.tierId = decodedToken.tier_id;
    user.tierName = decodedToken.tier_name;
    user.isCheckedIn = JSON.parse(decodedToken?.venue_checkedin);

    if (decodedToken.customer_tags && decodedToken.customer_tags !== '[]') {
      user.customerTags = JSON.parse(decodedToken.customer_tags);
    }

    return user;
  }

  /* Support V1 SSO login as a user without a username and password.*/
  ssoLoginV1(sskey: string, cc: string, returnUrl?: string) {
    this.logService.debug('Logging on with V1 SSO', {
      sskey,
      cc,
      returnUrl,
    });

    return this.api.post<any>(`sso/ssov1?sskey=${sskey}&cc=${cc}&returnUrl=${returnUrl}`, {}).pipe(
      tap((authResponse) => {
        if (authResponse) {
          if (authResponse.ErrorNumber != 0) {
            this.logService.error('Failed V1 SSO login', authResponse.ErrorDescription);
            this.router.navigate([returnUrl]);
          } else {
            const accessToken = authResponse.Data.AccessToken;
            this.loginUsingToken(accessToken, authResponse.Data.RefreshToken, '/');
          }
        }
      })
    );
  }

  private getAccessToken(): string {
    return localStorage.getItem('token') ?? '';
  }

  /**Switch Club ID with
   * @param clubId id of club
   *  and set new tokens to localStore*/
  switchClub(clubId: number) {
    let refreshToken = localStorage.getItem('refresh') as string;
    return !this.jwtHelper.isTokenExpired(refreshToken)
      ? this.api
          .post(`sso/switchclub/${clubId}`, `"${refreshToken}"`)
          .pipe(
            map((response: LoginResponse) => {
              return this.loginUsingToken(response.AccessToken, response.RefreshToken);
            })
          )
          .toPromise()
      : of();
  }

  getUnauthorizedEntityHeader() {
    const unauthorizedEntityGuid = sessionStorage.getItem('UnauthorizedEntityGuid');
    if (unauthorizedEntityGuid) {
      return this.api.getHeaders().set('UnauthorizedEntityGuid', unauthorizedEntityGuid);
    }
    return null;
  }
}
